import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getContractApiUrl } from '../utils/apiUrls';

const BACKEND_URL = getContractApiUrl();

const COLUMN_ORDER = [
    'id',
    'contract_id',
    'property_address',
    'fully_executed_date',
    'lenders_title_insurance_policy_payer',
    'mortgage_registration_tax_payer',
    'loan_origination_points_payer',
    'appraisal_fee_payer',
    'survey_payer',
    'home_inspection_payer',
    'recording_fee_deed_mortgage_payer',
    'escrow_prepaids_payer',
    'buyer_agent_commission_payer',
    'seller_agent_commission_payer',
    'title_abstracting_fee_payer',
    'documentary_stamp_tax_payer',
    'owners_title_insurance_policy_payer',
    'recording_release_fee_payer',
    'title_curative_requirements_payer',
    'hoa_resale_certificate_payer',
    'home_warranty_payer',
    'escrow_fees_payer',
    'property_taxes_payer',
    'created_at'
];

function DeveloperFees() {
    const { user, isAuthenticated, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();

    const [fees, setFees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Filters
    const [contractId, setContractId] = useState('');
    const [propertyAddress, setPropertyAddress] = useState('');
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');

    const isKeyFloUser = useMemo(() => {
        const email = user?.email || '';
        const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
        return ALLOWED_USERS.includes(email);
    }, [user]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[1]}-${m[2]}-${m[3]}`;
        return dateStr;
    };

    const formatDateTime = (ts) => {
        try {
            return new Date(ts).toLocaleString();
        } catch (e) {
            return ts || '';
        }
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        if (!isKeyFloUser) {
            navigate('/');
        }
    }, [isAuthenticated, isKeyFloUser, navigate]);

    const fetchFees = async () => {
        if (!isAuthenticated || !isKeyFloUser) return;
        try {
            setLoading(true);
            setError('');

            const params = {};
            if (contractId) params.contract_id = contractId;
            if (propertyAddress) params.property_address = propertyAddress;
            if (dateStart) params.date_start = dateStart;
            if (dateEnd) params.date_end = dateEnd;

            const response = await axios.get(`${BACKEND_URL}/api/fee-responsibility`, { params });

            if (response.data?.status !== 'success') {
                throw new Error(response.data?.message || 'Failed to fetch fees');
            }

            setFees(response.data.data || []);
        } catch (e) {
            setError(e.message || 'Failed to load fees');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && isKeyFloUser) fetchFees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isKeyFloUser]);

    if (!isAuthenticated) return null;
    if (!isKeyFloUser) return null;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2>Developer • Fee Responsibility (SQL)</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                    <button className="back-button" onClick={() => navigate('/')}>← Back</button>
                    <DeveloperNavigation />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Contract ID</label>
                    <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="contract_id" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Property Address</label>
                    <input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} placeholder="address" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Start Date (Executed)</label>
                    <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>End Date (Executed)</label>
                    <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} style={{ width: '100%' }} />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={fetchFees}>Apply Filters</button>
                <button onClick={() => { setContractId(''); setPropertyAddress(''); setDateStart(''); setDateEnd(''); fetchFees(); }}>Clear</button>
            </div>

            {loading && <LoadingSpinner />}
            {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
                <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                        <tr>
                            {COLUMN_ORDER.map((col) => (
                                <th key={col} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', whiteSpace: 'nowrap' }}>
                                    {col.replace(/_/g, ' ').toUpperCase()}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {fees.map((fee, idx) => (
                            <tr key={fee.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                {COLUMN_ORDER.map((col) => {
                                    let val = fee[col];
                                    if (col === 'created_at') val = val ? formatDateTime(val) : '';
                                    else if (col === 'fully_executed_date') val = val ? formatDate(val) : '';
                                    return (
                                        <td key={col} style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem', borderRight: '1px solid #f1f5f9' }}>
                                            {String(val || '')}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {fees.length === 0 && !loading && (
                            <tr>
                                <td colSpan={COLUMN_ORDER.length} style={{ padding: '12px', textAlign: 'center' }}>No fee records found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default DeveloperFees;
